import type { Href } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'

import type { TDefaultButtonProps } from '@/src/elements/button/default'
import type { TMessageProps } from '@/src/elements/message'
import { Message } from '@/src/elements/message/Message'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate, type TMessageKey } from '@/src/lang/useTranslate'

import { useBackHandlePress } from '../hooks/useBackHandlePress'
import { parseApiError } from '../utilities/fetcher/parseApiError'
import { cn } from '../utils/cn'
import { useRoutes } from '../utils/routes'

export type TErrorMessageProps = TMessageProps & {
  button?: TDefaultButtonProps[]
  fallbackRoute?: Href
  error?: Error | null
  field?: string
  defaultErrorKey?: TMessageKey
}

export const ErrorMessage = ({
  button,
  children,
  className,
  fallbackRoute,
  error,
  field,
  defaultErrorKey,
  ...props
}: PropsWithChildren<TErrorMessageProps>): ReactNode => {
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const { handleBackPress } = useBackHandlePress(fallbackRoute ?? routes['prayer-space']())

  const parsedErrorKey = (error && parseApiError(error, field)) || defaultErrorKey

  return (
    <Message
      colors="danger"
      iconName={ICON.ALERT_TRIANGLE}
      className={cn('m-4 w-5/6 max-w-96 self-center', className)}
      buttons={
        button || fallbackRoute
          ? [
              {
                children: t('word.previous'),
                onPress: handleBackPress,
                startIcon: ICON.ARROW_LEFT,
              },
              ...(button ?? []),
            ]
          : undefined
      }
      {...props}
    >
      {parsedErrorKey ? t(parsedErrorKey) : children}
    </Message>
  )
}
